import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo } from 'react';
import { CoverageMetricTableCell } from '@/features/codeCoverage/pages/CoverageMetricTableCell';
import {
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { type TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { getDetailLabel } from '@/shared/copy/details';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { s } from '@/theme/tokens';
import { formatDate } from '@/ui/formatters/formatDate';
import { Box, TableCell, TableRow } from '@/ui/primitives';
import type { CoverageRunHistoryRow } from '@/features/codeCoverage/pages/coverageRunHistoryRows';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel, GridSortModel } from '@/ui/primitives';
import type { QueryResult } from '@flowqa/shared';

type CoverageRunHistoryTableProps = {
  rows: CoverageRunHistoryRow[];
  queryKey: QueryKey;
  initialSortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
};

type CoverageRunHistoryColumnKey =
  | 'run'
  | 'lineCoverage'
  | 'functionCoverage'
  | 'branchCoverage'
  | 'statementCoverage'
  | 'details';

const coverageRunHistoryColumns: CoverageRunHistoryColumnKey[] = [
  'run',
  'lineCoverage',
  'functionCoverage',
  'branchCoverage',
  'statementCoverage',
  'details',
];

const columnWidths: Record<CoverageRunHistoryColumnKey, string> = {
  run: '26%',
  lineCoverage: '12%',
  functionCoverage: '12%',
  branchCoverage: '12%',
  statementCoverage: '12%',
  details: '26%',
};

const columnLabels: Record<CoverageRunHistoryColumnKey, string> = {
  run: getDetailLabel('run'),
  lineCoverage: 'Line',
  functionCoverage: 'Function',
  branchCoverage: 'Branch',
  statementCoverage: 'Statement',
  details: getDetailLabel('details'),
};

const skeletonColumnByKey: Record<CoverageRunHistoryColumnKey, TableV2SkeletonColumnKind> = {
  run: 'name',
  lineCoverage: 'metric',
  functionCoverage: 'metric',
  branchCoverage: 'metric',
  statementCoverage: 'metric',
  details: 'name',
};

const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : `${value.toFixed(2)}%`;

const formatCountPair = (
  covered: number | null | undefined,
  total: number | null | undefined,
): string => {
  if (covered === null || covered === undefined || total === null || total === undefined) {
    return '-';
  }

  return `${String(covered)} / ${String(total)}`;
};

const compareStrings = (
  leftValue: string | null | undefined,
  rightValue: string | null | undefined,
): number => (leftValue ?? '').localeCompare(rightValue ?? '', undefined, { sensitivity: 'base' });

const compareNumbers = (
  leftValue: number | null | undefined,
  rightValue: number | null | undefined,
): number => (leftValue ?? Number.NEGATIVE_INFINITY) - (rightValue ?? Number.NEGATIVE_INFINITY);

const compareDates = (
  leftValue: Date | null | undefined,
  rightValue: Date | null | undefined,
): number => (leftValue?.getTime() ?? 0) - (rightValue?.getTime() ?? 0);

const resolveCoverageRunHistorySorts = (
  initialSortModel?: GridSortModel,
): GridQueryInput['sorts'] => {
  const firstSort = initialSortModel?.[0];
  return [
    {
      field: firstSort?.field ?? 'capturedAt',
      direction: firstSort?.sort === 'asc' ? 'asc' : 'desc',
    },
  ];
};

const sortRows = (
  rows: CoverageRunHistoryRow[],
  input: GridQueryInput,
): CoverageRunHistoryRow[] => {
  const sort = input.sorts[0] ?? { field: 'capturedAt', direction: 'desc' as const };
  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...rows].sort((left, right) => {
    let result = 0;

    switch (sort.field) {
      case 'linesPct':
        result = compareNumbers(left.linesPct, right.linesPct);
        break;
      case 'functionsPct':
        result = compareNumbers(left.functionsPct, right.functionsPct);
        break;
      case 'branchesPct':
        result = compareNumbers(left.branchesPct, right.branchesPct);
        break;
      case 'statementsPct':
        result = compareNumbers(left.statementsPct, right.statementsPct);
        break;
      case 'branch':
        result = compareStrings(left.branch, right.branch);
        break;
      case 'capturedAt':
      default:
        result = compareDates(left.capturedAt, right.capturedAt);
        break;
    }

    return result * factor;
  });
};

const isColumnVisible = (
  columnKey: CoverageRunHistoryColumnKey,
  columnVisibilityModel?: GridColumnVisibilityModel,
): boolean => columnVisibilityModel?.[columnKey] !== false;

const getVisibleColumns = (
  columnVisibilityModel?: GridColumnVisibilityModel,
): CoverageRunHistoryColumnKey[] =>
  coverageRunHistoryColumns.filter((columnKey) =>
    isColumnVisible(columnKey, columnVisibilityModel),
  );

const buildColgroup = (visibleColumns: CoverageRunHistoryColumnKey[]): ReactElement => (
  <colgroup>
    {visibleColumns.map((columnKey) => (
      <col key={columnKey} width={columnWidths[columnKey]} />
    ))}
  </colgroup>
);

const buildHeader = (visibleColumns: CoverageRunHistoryColumnKey[]): ReactElement => (
  <TableRow>
    {visibleColumns.map((columnKey) => (
      <TableCell key={columnKey}>
        <TableV2HeaderLabel label={columnLabels[columnKey]} />
      </TableCell>
    ))}
  </TableRow>
);

const MetricCell = ({
  covered,
  percent,
  total,
}: {
  covered: number | null | undefined;
  percent: number | null | undefined;
  total: number | null | undefined;
}): ReactElement => (
  <CoverageMetricTableCell
    primary={formatPercent(percent)}
    secondary={formatCountPair(covered, total)}
  />
);

const CoverageRunHistoryTableRow = ({
  columnVisibilityModel,
  isLastVisibleRow,
  row,
}: {
  columnVisibilityModel?: GridColumnVisibilityModel;
  isLastVisibleRow: boolean;
  row: CoverageRunHistoryRow;
}): ReactElement => {
  const visibleColumns = getVisibleColumns(columnVisibilityModel);
  const runLabel = row.capturedAt
    ? formatDate(row.capturedAt, { includeTime: true })
    : 'Coverage Run';
  const secondaryLabel = `${row.branch ?? 'No branch'} · ${row.commitSha ?? 'No commit SHA'}`;

  return (
    <TableRow sx={buildTableV2BodyRowSurfaceSx({ hasRenderedDivider: !isLastVisibleRow })}>
      {visibleColumns.includes('run') ? (
        <TableCell>
          <TableV2NameCell
            href={`/coverage-runs/${row.runId}`}
            primary={runLabel}
            secondary={secondaryLabel}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('lineCoverage') ? (
        <TableCell>
          <MetricCell covered={row.linesCovered} percent={row.linesPct} total={row.linesTotal} />
        </TableCell>
      ) : null}
      {visibleColumns.includes('functionCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.functionsCovered}
            percent={row.functionsPct}
            total={row.functionsTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('branchCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.branchesCovered}
            percent={row.branchesPct}
            total={row.branchesTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('statementCoverage') ? (
        <TableCell>
          <MetricCell
            covered={row.statementsCovered}
            percent={row.statementsPct}
            total={row.statementsTotal}
          />
        </TableCell>
      ) : null}
      {visibleColumns.includes('details') ? (
        <TableCell>
          <Box sx={{ display: 'grid', gap: s('xs'), minWidth: 0 }}>
            <TableV2NameCell primary={row.sourceKey ?? '-'} secondary={row.coverageRunStatus} />
            <TableV2DateTimeCell value={row.capturedAt} />
          </Box>
        </TableCell>
      ) : null}
    </TableRow>
  );
};

export const CoverageRunHistoryTable = ({
  rows,
  queryKey,
  initialSortModel,
  columnVisibilityModel,
}: CoverageRunHistoryTableProps): ReactElement => {
  const visibleColumns = useMemo(
    () => getVisibleColumns(columnVisibilityModel),
    [columnVisibilityModel],
  );
  const sorts = useMemo(() => resolveCoverageRunHistorySorts(initialSortModel), [initialSortModel]);
  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<CoverageRunHistoryRow>> => {
      const sortedRows = sortRows(rows, input);
      const page = Math.max(input.page, 1);
      const pageSize = input.pageSize;
      const startIndex = (page - 1) * pageSize;

      return await Promise.resolve({
        items: sortedRows.slice(startIndex, startIndex + pageSize),
        total: sortedRows.length,
        page,
        pageSize,
      });
    },
    [rows],
  );

  return (
    <TableV2QueryShell
      active
      ariaLabel="Coverage Run History"
      colgroup={buildColgroup(visibleColumns)}
      emptyText={getEmptyStateMessage('entity.noneFound', { entity: 'coverageRun' })}
      entityLabel="coverage runs"
      header={buildHeader(visibleColumns)}
      pageSizeOptions={[10, 25, 50]}
      queryFn={queryFn}
      queryKey={queryKey}
      skeletonColumns={visibleColumns.map((columnKey) => skeletonColumnByKey[columnKey])}
      sorts={sorts}
      renderRow={(row, context) => (
        <CoverageRunHistoryTableRow
          key={row.id}
          columnVisibilityModel={columnVisibilityModel}
          isLastVisibleRow={context.isLastVisibleRow}
          row={row}
        />
      )}
    />
  );
};
